import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { randomUUID } from '@/utils/uuid';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { z } from 'zod';

import { AnalisisDatosGeneralesForm } from '@/features/analisis/common/components/AnalisisDatosGeneralesForm';
import { FotosManager } from '@/features/analisis/common/components/FotosManager';
import type { FotoTemporal, FotoTemporalDraft } from '@/features/analisis/common/fotosTypes';
import type { AnalisisDraft } from '@/features/analisis/common/hooks/useAnalisisDraft';
import { useAnalisisDraft } from '@/features/analisis/common/hooks/useAnalisisDraft';
import type { PoolLocal } from '@/features/analisis/baermann/components/BaermannPoolCard';
import { BaermannPoolsSection } from '@/features/analisis/baermann/components/BaermannPoolsSection';
import { fotosTemporalesApi } from '@/features/analisis/common/api/fotosTemporalesApi';
import { formatNumeroAnalisis } from '@/features/analisis/utils/formatNumeroAnalisis';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage, isApiError } from '@/lib/apiError';
import { TipoAnalisis } from '@/types/enums';

import {
  useCreateSedimentacionFh,
  useEditarCaptionFotoSedimentacionFh,
  useEliminarFotoSedimentacionFh,
  useSedimentacionFh,
  useUpdateSedimentacionFh,
  useUploadFotoSedimentacionFh,
} from '../hooks/useSedimentacionFh';

type TabId = 'datos' | 'pools' | 'fotos';

const MAX_TOTAL = 25;

const datosSchema = z.object({
  fecha_analisis: z.date({ message: 'Fecha de análisis inválida' }),
  identificacion_muestras: z.string().nullable(),
  alimentacion: z.string().nullable(),
  ultimo_antiparasitario: z.string().nullable(),
  fecha_ultimo_tratamiento: z.date().nullable(),
  comentarios_lote: z.string().nullable(),
});

type DatosValues = z.infer<typeof datosSchema>;

const DATOS_FIELDS: (keyof DatosValues)[] = [
  'fecha_analisis',
  'identificacion_muestras',
  'alimentacion',
  'ultimo_antiparasitario',
  'fecha_ultimo_tratamiento',
  'comentarios_lote',
];

const DEFAULT_DATOS: DatosValues = {
  fecha_analisis: new Date(),
  identificacion_muestras: null,
  alimentacion: null,
  ultimo_antiparasitario: null,
  fecha_ultimo_tratamiento: null,
  comentarios_lote: null,
};

type SedimentacionFhDraftData = {
  datosValues: Omit<DatosValues, 'fecha_analisis' | 'fecha_ultimo_tratamiento'> & {
    fecha_analisis: string;
    fecha_ultimo_tratamiento: string | null;
  };
  pools: PoolLocal[];
  fotosTemporales: FotoTemporalDraft[];
};

function makeEmptyPool(): PoolLocal {
  return { id: randomUUID(), numeros_muestras: [], valor: null, observacion: null };
}

function validatePools(pools: PoolLocal[]): string | null {
  if (pools.some((p) => p.valor === null)) return 'Hay pools sin resultado.';
  return null;
}

interface SedimentacionFhFormDialogProps {
  open: boolean;
  onClose: () => void;
  uidProtocolo: string;
  uidAnalisis?: string;
  numeroProtocolo?: number;
  establecimientoNombre?: string;
  onSuccess?: () => void;
}

export function SedimentacionFhFormDialog({
  open,
  onClose,
  uidProtocolo,
  uidAnalisis,
  numeroProtocolo,
  establecimientoNombre,
  onSuccess,
}: SedimentacionFhFormDialogProps): JSX.Element {
  const isEdit = !!uidAnalisis;
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const notify = useNotify();

  const sedFhQuery = useSedimentacionFh(uidProtocolo, uidAnalisis);
  const createMutation = useCreateSedimentacionFh();
  const updateMutation = useUpdateSedimentacionFh();

  const [tabActiva, setTabActiva] = useState<TabId>('datos');
  const editInitializedRef = useRef(false);
  const [pools, setPools] = useState<PoolLocal[]>(() => [makeEmptyPool()]);
  const [poolsDirty, setPoolsDirty] = useState(false);
  const [fotosTemporales, setFotosTemporales] = useState<FotoTemporal[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showLockedDialog, setShowLockedDialog] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [pendingDraft, setPendingDraft] = useState<AnalisisDraft<SedimentacionFhDraftData> | null>(null);

  const { saveDraft, loadDraft, clearDraft } = useAnalisisDraft<SedimentacionFhDraftData>('sedimentacion-fh');

  const methods = useForm<DatosValues>({
    resolver: zodResolver(datosSchema),
    defaultValues: { ...DEFAULT_DATOS, fecha_analisis: new Date() },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, errors, submitCount },
  } = methods;

  const watchedAll = useWatch({ control });

  useEffect(() => {
    if (open && !uidAnalisis) {
      const d = loadDraft(uidProtocolo);
      setPendingDraft(d);
      reset({ ...DEFAULT_DATOS, fecha_analisis: new Date() });
      setPools([makeEmptyPool()]);
      setPoolsDirty(false);
      setFotosTemporales([]);
      setTabActiva('datos');
      setShowErrors(false);
      setSubmitError(null);
    }
    if (!open) {
      editInitializedRef.current = false;
    }
  }, [open, uidAnalisis, uidProtocolo, reset, loadDraft]);

  // Auto-save draft in create mode
  useEffect(() => {
    if (!open || isEdit) return;
    saveDraft(uidProtocolo, {
      datosValues: {
        ...watchedAll,
        fecha_analisis: watchedAll.fecha_analisis?.toISOString() ?? '',
        fecha_ultimo_tratamiento: watchedAll.fecha_ultimo_tratamiento?.toISOString() ?? null,
      },
      pools,
      fotosTemporales: fotosTemporales
        .filter((f) => f.uploadStatus === 'done' && f.uid_foto !== '')
        .map((f) => ({ localId: f.localId, uid_foto: f.uid_foto, content_type: f.file.type, caption: f.caption })),
    });
  }, [watchedAll, pools, fotosTemporales, open, isEdit, saveDraft, uidProtocolo]);

  useEffect(() => {
    if (isEdit && sedFhQuery.data) {
      if (editInitializedRef.current) return;
      editInitializedRef.current = true;
      const s = sedFhQuery.data;
      reset({
        fecha_analisis: parseISO(s.fecha_analisis),
        identificacion_muestras: s.identificacion_muestras,
        alimentacion: s.alimentacion,
        ultimo_antiparasitario: s.ultimo_antiparasitario,
        fecha_ultimo_tratamiento: s.fecha_ultimo_tratamiento
          ? parseISO(s.fecha_ultimo_tratamiento)
          : null,
        comentarios_lote: s.comentarios_lote,
      });
      const loadedPools = s.pools.map((p) => ({
        id: randomUUID(),
        numeros_muestras: p.numeros_muestras,
        valor: p.valor,
        observacion: p.observacion,
      }));
      setPools(loadedPools.length > 0 ? loadedPools : [makeEmptyPool()]);
      setPoolsDirty(false);
      setFotosTemporales([]);
      setTabActiva('datos');
      setShowErrors(false);
      setSubmitError(null);
    }
  }, [isEdit, sedFhQuery.data, reset]);

  // Warn before tab close when there are unsaved changes
  useEffect(() => {
    if (!isDirty && !poolsDirty && fotosTemporales.length === 0) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, poolsDirty, fotosTemporales.length]);

  const isPending = createMutation.isPending || updateMutation.isPending;
  const hasPendingUploads = fotosTemporales.some((f) => f.uploadStatus === 'uploading');
  const hasAttemptedSubmit = submitCount > 0;
  const tabDatosHasError = hasAttemptedSubmit && DATOS_FIELDS.some((f) => !!errors[f]);
  const tabPoolsHasError = showErrors && validatePools(pools) !== null;

  function handlePoolsChange(updated: PoolLocal[]): void {
    setPools(updated);
    setPoolsDirty(true);
  }

  function handleClose(): void {
    if (isDirty || poolsDirty || fotosTemporales.length > 0) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  }

  function handleConfirmDiscard(): void {
    setShowDiscardDialog(false);
    onClose();
  }

  async function handleRestoreDraft(): Promise<void> {
    if (!pendingDraft) return;
    const d = pendingDraft.data;
    reset({
      ...d.datosValues,
      fecha_analisis: parseISO(d.datosValues.fecha_analisis),
      fecha_ultimo_tratamiento: d.datosValues.fecha_ultimo_tratamiento
        ? parseISO(d.datosValues.fecha_ultimo_tratamiento)
        : null,
    });
    setPools(d.pools.length > 0 ? d.pools : [makeEmptyPool()]);
    if (d.fotosTemporales.length > 0) {
      const restoredFotos = await Promise.all(
        d.fotosTemporales.map(async (f) => {
          try {
            const { presigned_url } = await fotosTemporalesApi.getFotoTemporal(f.uid_foto);
            return {
              localId: f.localId,
              uid_foto: f.uid_foto,
              blobUrl: presigned_url,
              file: new File([], '', { type: f.content_type }),
              caption: f.caption,
              uploadStatus: 'done' as const,
            } satisfies FotoTemporal;
          } catch {
            return {
              localId: f.localId,
              uid_foto: f.uid_foto,
              blobUrl: '',
              file: new File([], '', { type: f.content_type }),
              caption: f.caption,
              uploadStatus: 'done' as const,
            } satisfies FotoTemporal;
          }
        }),
      );
      setFotosTemporales(restoredFotos);
    }
    setTabActiva('datos');
    setPendingDraft(null);
  }

  function handleDiscardDraft(): void {
    clearDraft(uidProtocolo);
    setPendingDraft(null);
  }

  function handleSaveError(errs: FieldErrors<DatosValues>): void {
    if (DATOS_FIELDS.some((f) => !!errs[f])) setTabActiva('datos');
  }

  async function handleSave(values: DatosValues): Promise<void> {
    setSubmitError(null);

    const poolsError = validatePools(pools);
    if (poolsError) {
      setShowErrors(true);
      setSubmitError(new Error(poolsError));
      setTabActiva('pools');
      return;
    }

    const mappedPools = pools.map((p) => ({
      numeros_muestras: p.numeros_muestras,
      valor: p.valor!,
      observacion: p.observacion,
    }));

    const doneFotos = fotosTemporales.filter((f) => f.uploadStatus === 'done');

    try {
      if (isEdit && uidAnalisis) {
        await updateMutation.mutateAsync({
          uidProtocolo,
          uidAnalisis,
          data: {
            fecha_analisis: values.fecha_analisis.toISOString(),
            identificacion_muestras: values.identificacion_muestras,
            alimentacion: values.alimentacion,
            ultimo_antiparasitario: values.ultimo_antiparasitario,
            fecha_ultimo_tratamiento: values.fecha_ultimo_tratamiento?.toISOString() ?? null,
            comentarios_lote: values.comentarios_lote,
            pools: mappedPools,
            uid_analisis_hpg_referencia: null,
          },
        });
        notify.success('Análisis Sedimentación Fh actualizado.');
      } else {
        await createMutation.mutateAsync({
          uidProtocolo,
          data: {
            tipo_analisis: TipoAnalisis.SEDIMENTACION_FH,
            fecha_analisis: values.fecha_analisis.toISOString(),
            identificacion_muestras: values.identificacion_muestras,
            alimentacion: values.alimentacion,
            ultimo_antiparasitario: values.ultimo_antiparasitario,
            fecha_ultimo_tratamiento: values.fecha_ultimo_tratamiento?.toISOString() ?? null,
            comentarios_lote: values.comentarios_lote,
            pools: mappedPools,
            fotos: doneFotos.map((f) => ({ uid_foto: f.uid_foto, content_type: f.file.type, caption: f.caption })),
            uid_analisis_hpg_referencia: null,
          },
        });
        notify.success('Análisis Sedimentación Fh creado.');
        clearDraft(uidProtocolo);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      if (isApiError(err) && err.isConflict()) {
        setShowLockedDialog(true);
      } else {
        setSubmitError(err);
      }
    }
  }

  const subtitleParts = [
    numeroProtocolo != null ? `Protocolo N° ${numeroProtocolo}` : null,
    establecimientoNombre ?? null,
  ].filter(Boolean);
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : null;

  const dialogTitle = (() => {
    if (!isEdit) return 'Nuevo análisis Sedimentación Fh';
    const numFmt =
      numeroProtocolo != null
        ? formatNumeroAnalisis(numeroProtocolo, sedFhQuery.data?.numero_analisis ?? null)
        : null;
    return numFmt
      ? `Editar análisis Sedimentación Fh ${numFmt}`
      : 'Editar análisis Sedimentación Fh';
  })();

  const isLoadingContent = isEdit && sedFhQuery.isLoading;
  const isErrorContent = isEdit && sedFhQuery.isError;
  const showForm = !isLoadingContent && !isErrorContent;

  const datosLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>Datos del análisis</span>
      {tabDatosHasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
    </Box>
  );

  const poolsLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>Pools</span>
      {tabPoolsHasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
    </Box>
  );

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        fullScreen={isSmall}
        onClose={isPending ? undefined : handleClose}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pb: subtitle ? 1 : 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>{dialogTitle}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            aria-label="Cerrar"
            size="small"
            sx={{ mt: 0.5 }}
            disabled={isPending}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {showForm && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabActiva} onChange={(_, v: TabId) => setTabActiva(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ px: 3 }}>
              <Tab value="datos" label={datosLabel} />
              <Tab value="pools" label={poolsLabel} />
              <Tab value="fotos" label="Fotos" />
            </Tabs>
          </Box>
        )}

        {showForm && pendingDraft && (
          <Box sx={{ px: 3, pt: 2, pb: 2 }}>
            <Alert
              severity="info"
              sx={{
                bgcolor: 'rgba(90, 28, 112, 0.10)',
                color: '#3d1350',
                '& .MuiAlert-icon': { color: '#5A1C70' },
              }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={() => void handleRestoreDraft()}>
                    Restaurar
                  </Button>
                  <Button size="small" color="inherit" onClick={handleDiscardDraft}>
                    Descartar
                  </Button>
                </Box>
              }
            >
              Tenés un borrador guardado{' '}
              {formatDistanceToNow(new Date(pendingDraft.timestamp), {
                addSuffix: true,
                locale: es,
              })}.
            </Alert>
          </Box>
        )}

        <DialogContent dividers sx={{ px: 3, py: 1 }}>
          {isLoadingContent && (
            <Box>
              <Skeleton variant="rounded" height={200} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={320} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={160} />
            </Box>
          )}

          {isErrorContent && (
            <Alert severity="error">{getErrorMessage(sedFhQuery.error)}</Alert>
          )}

          {showForm && (
            <>
              {submitError != null && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {String(getErrorMessage(submitError))}
                </Alert>
              )}

              {tabActiva === 'datos' && <AnalisisDatosGeneralesForm noPaper />}

              {tabActiva === 'pools' && (
                <BaermannPoolsSection
                  pools={pools}
                  onChange={handlePoolsChange}
                  showErrors={showErrors}
                  disabled={isPending}
                  maxTotal={MAX_TOTAL}
                  hideMuestras
                />
              )}

              {tabActiva === 'fotos' && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Fotos del análisis
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Adjuntá imágenes de los huevos identificados (opcional). Podés vincular cada
                    foto a un pool por el caption.
                  </Typography>
                  <FotosManager
                    fotos={sedFhQuery.data?.fotos ?? []}
                    fotosTemporales={fotosTemporales}
                    onFotosTemporalesChange={setFotosTemporales}
                    uidProtocolo={uidProtocolo}
                    uidAnalisis={uidAnalisis ?? null}
                    useUploadFoto={useUploadFotoSedimentacionFh}
                    useEditarCaption={useEditarCaptionFotoSedimentacionFh}
                    useEliminarFoto={useEliminarFotoSedimentacionFh}
                    disabled={isPending || sedFhQuery.isFetching}
                  />
                </Paper>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={
              isPending ||
              hasPendingUploads ||
              (isEdit && !isDirty && !poolsDirty && fotosTemporales.length === 0)
            }
            onClick={() => void handleSubmit(handleSave, handleSaveError)()}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {hasPendingUploads
              ? 'Subiendo fotos...'
              : isPending
                ? 'Guardando...'
                : isEdit
                  ? 'Guardar cambios'
                  : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDiscardDialog} maxWidth="xs" onClose={() => setShowDiscardDialog(false)}>
        <DialogTitle>Descartar cambios</DialogTitle>
        <DialogContent>
          <DialogContentText>¿Descartar los cambios sin guardar?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardDialog(false)}>Seguir editando</Button>
          <Button color="error" onClick={handleConfirmDiscard}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showLockedDialog} maxWidth="xs">
        <DialogTitle>Protocolo cerrado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Este protocolo está cerrado y ya no admite cambios.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowLockedDialog(false);
              onClose();
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
